import React, { useState, useEffect } from 'react';
import { User, Division, Team } from '../../types';
import { getTeams, updateUserProfile } from '../../services/firebase';
import { User as UserIcon, Users, Trophy, ChevronRight } from 'lucide-react';

interface Props {
  user: User;
  onNext: () => void;
}

const ProfileSetupStep: React.FC<Props> = ({ user, onNext }) => {
  const [name, setName] = useState(user.name);
  const [division, setDivision] = useState<Division>(user.division || Division.BASIC);
  const [teamId, setTeamId] = useState<string>(user.teamId || '');
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const fetchedTeams = await getTeams();
        setTeams(fetchedTeams);
      } catch (error) {
        console.error("Failed to fetch teams", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSubmitting(true);
    try {
      await updateUserProfile(user.id, {
        name,
        division,
        teamId
      });
      onNext();
    } catch (error) {
      console.error("Failed to update profile", error);
      // Ideally show toast or error
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col animate-fade-in relative">
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-2 custom-scrollbar pb-24 md:pb-6 md:grid md:grid-cols-2 md:gap-12 md:px-8 md:items-start md:content-center">

          {/* Left Column: Personal Info */}
          <div className="space-y-6 md:space-y-8 animate-slide-up">
            <div className="text-center md:text-left">
              <h2 className="text-3xl font-bold text-slate-800 mb-2">Thiết lập hồ sơ</h2>
              <p className="text-slate-500">Cập nhật thông tin để bắt đầu thi đấu</p>
            </div>

            <div className="space-y-6">
              {/* Name Input */}
              <div className="space-y-2 animate-slide-up [animation-delay:100ms]">
                <label className="block text-sm font-bold text-slate-700">Tên hiển thị</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-3 text-slate-400" size={20} />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium text-slate-800"
                    placeholder="Nhập tên của bạn"
                    required
                  />
                </div>
              </div>

              {/* Team Selection */}
              <div className="space-y-2 animate-slide-up [animation-delay:300ms]">
                <label className="block text-sm font-bold text-slate-700">Chọn Team (Min 30km/thành viên)</label>
                <div className="relative">
                  <Users className="absolute left-3 top-3 text-slate-400" size={20} />
                  <select
                    value={teamId}
                    onChange={(e) => setTeamId(e.target.value)}
                    disabled={loading}
                    className="w-full pl-10 pr-10 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium text-slate-800 appearance-none disabled:bg-slate-50 disabled:text-slate-400"
                  >
                    <option value="">-- Chọn Team --</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.members?.length || 0} thành viên)
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-3.5 pointer-events-none text-slate-400">
                    <ChevronRight className="rotate-90" size={16} />
                  </div>
                </div>
                <p className="text-xs text-slate-400 pl-1">Bạn có thể gia nhập hoặc thay đổi Team sau này.</p>
              </div>
            </div>
          </div>

          {/* Right Column: Division Selection */}
          <div className="space-y-3 animate-slide-up [animation-delay:200ms] mt-6 md:mt-0 md:bg-slate-50 md:p-6 md:rounded-2xl md:h-full">
            <label className="block text-sm font-bold text-slate-700 mb-4">Hạng mục đăng ký</label>
            <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
              <div
                onClick={() => setDivision(Division.BASIC)}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all flex flex-col md:flex-row items-center text-center md:text-left gap-2 md:gap-4 ${division === Division.BASIC
                  ? 'border-indigo-500 bg-indigo-50/50'
                  : 'border-slate-100 bg-white hover:border-slate-200'
                  }`}
              >
                <div className={`p-2 rounded-full ${division === Division.BASIC ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                  <UserIcon size={20} />
                </div>
                <div>
                  <span className={`block font-bold ${division === Division.BASIC ? 'text-indigo-900' : 'text-slate-600'}`}>Cơ bản</span>
                  <span className={`text-xs ${division === Division.BASIC ? 'text-indigo-700' : 'text-slate-400'}`}>Min 40km</span>
                </div>
              </div>

              <div
                onClick={() => setDivision(Division.ADVANCED)}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all flex flex-col md:flex-row items-center text-center md:text-left gap-2 md:gap-4 ${division === Division.ADVANCED
                  ? 'border-indigo-500 bg-indigo-50/50'
                  : 'border-slate-100 bg-white hover:border-slate-200'
                  }`}
              >
                <div className={`p-2 rounded-full ${division === Division.ADVANCED ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                  <Trophy size={20} />
                </div>
                <div>
                  <span className={`block font-bold ${division === Division.ADVANCED ? 'text-indigo-900' : 'text-slate-600'}`}>Nâng cao</span>
                  <span className={`text-xs ${division === Division.ADVANCED ? 'text-indigo-700' : 'text-slate-400'}`}>Min 80km</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex-none p-4 w-full bg-white/80 backdrop-blur-md border-t border-slate-100 md:bg-transparent md:border-none z-10 animate-slide-up [animation-delay:400ms]">
          <button
            type="submit"
            disabled={submitting}
            className="w-full max-w-md mx-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg shadow-indigo-500/30 transition-all transform hover:-translate-y-1 active:scale-95 disabled:opacity-70 disabled:pointer-events-none flex items-center justify-center gap-2"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Tiếp tục <ChevronRight size={18} />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileSetupStep;
